import { AdsAssistantPostDeleteButton } from "@/components/ads-assistant/AdsAssistantPostDeleteButton";
import { AdsAssistantPostTemplatePicker } from "@/components/ads-assistant/AdsAssistantPostTemplatePicker";
import type { AdsAssistantSocialPostRow, AdsAssistantTemplateRow } from "@/lib/adsAssistantClient";
import { patchAdsSocialPost, postAdsSaleClaim } from "@/lib/adsAssistantClient";

export type AdsPostActionsGenerateOpen = {
  templateId: string;
  templateName: string | null;
  socialPostId: string;
};

export function AdsAssistantAggregationPostActions(props: {
  post: AdsAssistantSocialPostRow;
  templateName: string | null;
  scopedOrgId?: string | null;
  canIngestPosts?: boolean;
  canReassignAttribution?: boolean;
  onReassignAttribution?: (post: AdsAssistantSocialPostRow) => void;
  canQuality?: boolean;
  /** Slice 19a — trainer+ can link an existing indexed post to any active template. */
  canSetTemplate?: boolean;
  templates?: AdsAssistantTemplateRow[];
  busyId: string | null;
  busyGreenId: string | null;
  setBusyId: (id: string | null) => void;
  setBusyGreenId: (id: string | null) => void;
  setKbPost: (p: AdsAssistantSocialPostRow | null) => void;
  onCelebration?: (message: string) => void;
  onReload?: () => void;
  onRequestGenerate?: (p: AdsPostActionsGenerateOpen) => void;
}) {
  const {
    post: p,
    templateName,
    scopedOrgId,
    canIngestPosts,
    canReassignAttribution,
    onReassignAttribution,
    canQuality,
    canSetTemplate,
    templates,
    busyId,
    busyGreenId,
    setBusyId,
    setBusyGreenId,
    setKbPost,
    onCelebration,
    onReload,
    onRequestGenerate,
  } = props;

  return (
    <div className="mt-2.5 flex flex-wrap gap-3 border-t border-gray-100 pt-2 dark:border-zinc-700/80">
      {canIngestPosts ? (
        <AdsAssistantPostDeleteButton
          postId={p.id}
          disabled={busyId === p.id || busyGreenId === p.id}
          onDeleted={() => onReload?.()}
        />
      ) : null}
      {canReassignAttribution ? (
        <button
          type="button"
          className="text-[11px] font-medium text-indigo-800 hover:underline dark:text-indigo-300"
          onClick={() => onReassignAttribution?.(p)}
        >
          Reassign
        </button>
      ) : null}
      {scopedOrgId ? (
        <button
          type="button"
          className="text-[11px] font-medium text-gray-700 hover:underline dark:text-gray-300"
          onClick={() => setKbPost(p)}
        >
          KB ideas
        </button>
      ) : null}
      <button
        type="button"
        disabled={busyId === p.id}
        className="text-[11px] font-medium text-blue-700 hover:underline disabled:opacity-50 dark:text-blue-400"
        onClick={() => {
          void (async () => {
            setBusyId(p.id);
            const r = await postAdsSaleClaim({ social_post_id: p.id });
            setBusyId(null);
            if (!r.ok) {
              window.alert(r.error);
              return;
            }
            const c = r.data.celebration;
            const msg = `Congrats ${c.display_name}!${c.template_name ? ` They used template “${c.template_name}”.` : ""}`;
            onCelebration?.(msg);
            onReload?.();
          })();
        }}
      >
        {busyId === p.id ? "Saving…" : "+1 sale"}
      </button>
      {canQuality ? (
        <button
          type="button"
          disabled={busyGreenId === p.id}
          className="text-[11px] font-medium text-emerald-800 hover:underline disabled:opacity-50 dark:text-emerald-400"
          onClick={() => {
            void (async () => {
              setBusyGreenId(p.id);
              const next = !p.green_portable;
              const r = await patchAdsSocialPost(p.id, { green_portable: next });
              setBusyGreenId(null);
              if (!r.ok) {
                window.alert(r.error);
                return;
              }
              onReload?.();
            })();
          }}
        >
          {busyGreenId === p.id
            ? "Updating…"
            : p.green_portable
              ? "Unmark green (portable)"
              : "Mark green (portable)"}
        </button>
      ) : null}
      {p.template_id && onRequestGenerate ? (
        <button
          type="button"
          className="text-[11px] font-medium text-violet-700 hover:underline dark:text-violet-400"
          onClick={() =>
            onRequestGenerate({
              templateId: p.template_id!,
              templateName,
              socialPostId: p.id,
            })
          }
        >
          Generate similar
        </button>
      ) : null}
      {canSetTemplate && templates && templates.length ? (
        <AdsAssistantPostTemplatePicker
          post={p}
          templates={templates}
          currentTemplateName={templateName}
          onChanged={() => onReload?.()}
        />
      ) : null}
    </div>
  );
}
